import { Phone, Mail, MapPin, Facebook, Youtube } from 'lucide-react';

const LOGO_URL = "/logo.webp";

export const Footer = () => {
  return (
    <footer 
      className="bg-gray-900 text-white py-16"
      data-testid="footer"
      id="about"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <img 
              src={LOGO_URL} 
              alt="Appliance Direct" 
              className="h-10 w-auto mb-6"
            />
            <p className="text-gray-400 text-sm leading-relaxed max-w-md mb-6">
              Florida's trusted source for premium HVAC systems. We offer top-quality 
              Goodman heat pump systems with professional installation and industry-leading warranties.
            </p>
            <p className="text-sm font-semibold text-emerald-400 mb-1">
              FLORIDA BREEZE LIC#CAC-1814113
            </p>
            <p className="text-sm font-semibold text-emerald-400 mb-6">
              STRADA SERVICES LIC #CAC043953
            </p>
            {/* Social Icons */}
            <div className="flex items-center gap-4">
              <a 
                href="https://www.facebook.com/appliancedirect" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-emerald-600 rounded-full flex items-center justify-center transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="https://www.youtube.com/@SamPakApplianceDirectTV" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-emerald-600 rounded-full flex items-center justify-center transition-colors"
                aria-label="YouTube"
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>

            {/* Installation Partners */}
            <div className="mt-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_cooling-converter/artifacts/85z1nrng_resized_600x60.png"
                alt="Special Thank You to our trusted installation partners - Strada Services LIC#CAC043953 and Florida Breeze Air & Heat LIC#CAC-1814113"
                className="max-w-md w-full h-auto"
              />
            </div>
          </div>

          {/* Contact & Services Column */}
          <div>
            <h4 className="font-semibold text-lg mb-6">
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href="mailto:info@appliancedirect.com" 
                  className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors text-sm"
                >
                  <Mail className="w-4 h-4 text-emerald-400" />
                  info@appliancedirect.com
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-gray-400 text-sm">
                  <MapPin className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>
                    Serving all of Central Florida
                  </span>
                </div>
              </li>
            </ul>

            <h4 className="font-semibold text-lg mt-8 mb-4">
              Our Services
            </h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li>AC Installation Near Me</li>
              <li>New AC Unit Installation</li>
              <li>Home AC Installation</li>
              <li>HVAC Installation Near Me</li>
              <li>AC System Installation</li>
              <li>Air Conditioner Installation</li>
            </ul>
          </div>

          {/* Legal Column */}
          <div>
            <h4 className="font-semibold text-lg mb-6">
              Legal
            </h4>
            <ul className="space-y-3">
              <li>
                <a 
                  href="/terms" 
                  className="text-gray-400 hover:text-white transition-colors text-sm"
                  data-testid="footer-terms-link"
                >
                  Terms & Conditions
                </a>
              </li>
              <li>
                <a 
                  href="/privacy" 
                  className="text-gray-400 hover:text-white transition-colors text-sm"
                  data-testid="footer-privacy-link"
                >
                  Privacy Policy
                </a>
              </li>
              <li>
                <a 
                  href="/accessibility" 
                  className="text-gray-400 hover:text-white transition-colors text-sm"
                  data-testid="footer-accessibility-link"
                >
                  Accessibility Statement
                </a>
              </li>
            </ul>
          </div>

          {/* Areas We Service Column */}
          <div>
            <h4 className="font-semibold text-lg mb-6">
              Areas We Service
            </h4>
            <ul className="space-y-2">
              {[
                { name: 'Brevard County', slug: 'brevard-county' },
                { name: 'Orange County', slug: 'orange-county' },
                { name: 'Osceola County', slug: 'osceola-county' },
                { name: 'Seminole County', slug: 'seminole-county' },
                { name: 'Polk County', slug: 'polk-county' },
                { name: 'Lake County', slug: 'lake-county' },
                { name: 'Marion County', slug: 'marion-county' },
                { name: 'Sumter County', slug: 'sumter-county' },
                { name: 'Putnam County', slug: 'putnam-county' },
                { name: 'Volusia County', slug: 'volusia-county' },
                { name: 'Flagler County', slug: 'flagler-county' },
              ].map(area => (
                <li key={area.slug}>
                  <a
                    href={`/ac-installation/${area.slug}`}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                    data-testid={`footer-area-${area.slug}`}
                  >
                    {area.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Appliance Direct. All rights reserved.
            </p>
            <p className="text-gray-500 text-sm">
              "If You Didn't Buy Direct, You Paid Too Much!"
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
